import React, { useEffect, useState } from "react";
import { Dialog, useRunner } from "./parts";
import { roomsApi } from "./useRoom";
import { BLOCKS, recapDate, parseRecapDate } from "./roomModel.mjs";
import { shortDate } from "../record/format.mjs";

/*
 * THE CALL RECAP, BEFORE IT GOES ON AND AFTER (RecapPreview and EditDialogs
 * boards). Add: the server reads his follow-up into the four blocks
 * (src/rooms/recap.js) and this shows them with what was cut; he edits and adds.
 * Edit: the same fields, "Reset to the email" puts the first reading back.
 *
 * Props: companyId, companyName, activityId (add) | item (edit), onClose
 */
export default function RecapDialog({ companyId, companyName, activityId = null, item = null, onClose }) {
  const editing = !!item;
  const [recap, setRecap] = useState(editing ? item.content : null);
  const [title, setTitle] = useState(editing ? item.content.title || "" : "");
  const [date, setDate] = useState(editing ? recapDate(item.content.date) : "");
  const [blocks, setBlocks] = useState(editing ? { ...item.content.blocks } : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);

  useEffect(() => {
    if (editing) return;
    let live = true;
    roomsApi
      .recapDraft(companyId, activityId)
      .then(({ recap: r }) => {
        if (!live) return;
        setRecap(r);
        setTitle(r.title || "");
        setDate(recapDate(r.date));
        setBlocks({ ...r.blocks });
      })
      .catch((e) => live && setErr(e.message));
    return () => {
      live = false;
    };
  }, [editing, companyId, activityId]);

  const original = recap && recap.original ? recap.original : null;
  const src = recap && recap.source ? recap.source : null;
  const changed = (k) => original && blocks && String(blocks[k] || "").trim() !== String(original[k] || "").trim();
  const empty = !blocks || !Object.values(blocks).some((v) => String(v || "").trim());

  const payload = () => ({
    ...recap,
    title: title.trim() || "Call",
    date: parseRecapDate(date, { fallback: recap && recap.date }),
    blocks,
  });

  const save = () =>
    run(async () => {
      if (editing) await roomsApi.editItem(companyId, item.id, { recap: payload() });
      else await roomsApi.add(companyId, { section: "recaps", recap: payload() });
      onClose(true);
    });

  const reset = () =>
    run(async () => {
      await roomsApi.editItem(companyId, item.id, { reset: true });
      onClose(true);
    });

  const sub = src ? `Cleaned from your follow-up · “${src.subject || "(no subject)"}” · ${shortDate(src.at)}` : editing ? "Written by hand" : "Reading your follow-up…";

  return (
    <Dialog
      title={editing ? "Edit call recap" : `Add call recap to ${companyName}’s room`}
      eyebrow={editing ? "Call recap" : null}
      sub={sub}
      onClose={() => onClose(false)}
      foot={
        <>
          {editing ? (
            <span className="rm-sub">
              {item.edited_at ? `Last edited ${shortDate(item.edited_at)}` : `Added ${shortDate(item.created_at)}`}
              {original && (
                <>
                  {" · "}
                  <button type="button" className="rm-link rm-link--strong" disabled={busy} onClick={reset}>Reset to the email</button>
                </>
              )}
            </span>
          ) : (
            <span className="rm-sub">Goes on top of Call recaps. {companyName} sees it the next time they open the room.</span>
          )}
          {err && <span className="rm-err">{err}</span>}
          <button type="button" className="rm-btn" onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy || empty || !blocks} onClick={save}>
            {busy ? "Saving…" : editing ? "Save" : "Add to room"}
          </button>
        </>
      }
    >
      {!blocks ? (
        <p className="rm-sub">{err ? "" : "Reading the email into the four blocks…"}</p>
      ) : (
        <>
          <div className="rm-grid2">
            <div className="rm-field">
              <label htmlFor="rm-r-title">Call</label>
              <input id="rm-r-title" className="rm-input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="rm-field">
              <label htmlFor="rm-r-date">Date</label>
              <input id="rm-r-date" className="rm-input" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          {BLOCKS.map((b) => (
            <div className="rm-field" key={b.key}>
              <label htmlFor={`rm-r-${b.key}`}>{b.label}</label>
              <textarea
                id={`rm-r-${b.key}`}
                className={`rm-textarea${changed(b.key) ? " rm-edited" : ""}`}
                rows={b.rows}
                value={blocks[b.key] || ""}
                onChange={(e) => setBlocks((x) => ({ ...x, [b.key]: e.target.value }))}
              />
              {changed(b.key) ? (
                <div className="rm-hint">Edited by you · the email is not changed</div>
              ) : b.hint ? (
                <div className="rm-hint">{b.hint}</div>
              ) : !String(blocks[b.key] || "").trim() ? (
                <div className="rm-hint">Empty: left off the room</div>
              ) : null}
            </div>
          ))}
          {!editing && recap && recap.left_out && recap.left_out.length > 0 && (
            <details className="rm-left">
              <summary>
                What was left out <span>· {recap.left_out.length} {recap.left_out.length === 1 ? "line" : "lines"}</span>
              </summary>
              <div className="rm-left__list">
                {recap.left_out.map((l, i) => (
                  <span key={i}>“{l}”</span>
                ))}
              </div>
            </details>
          )}
          {!editing && recap && recap.read_by === "partial" && (
            <div className="rm-hint">The email has no “What we heard / Next step” headings, so only what could be placed is filled in. Fill the rest from the email.</div>
          )}
        </>
      )}
    </Dialog>
  );
}
